import { useRef, useState, useCallback } from 'react';

const audioCache = new Map();
const MAX_CACHE = 200;

function getCacheKey(text, voice, rate, pitch) {
  return `${voice}|${rate}|${pitch}|${text}`;
}

function fallbackSpeak(text, onEnd) {
  if (typeof window === 'undefined' || !window.speechSynthesis) return;
  const utt = new SpeechSynthesisUtterance(text);
  utt.rate = 0.9;
  utt.pitch = 1.0;
  utt.lang = 'en-US';
  if (onEnd) utt.onend = onEnd;
  window.speechSynthesis.speak(utt);
}

export function useTTS() {
  const audioRef = useRef(null);
  const [speaking, setSpeaking] = useState(false);

  const stop = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      audioRef.current = null;
    }
    if (typeof window !== 'undefined' && window.speechSynthesis) {
      window.speechSynthesis.cancel();
    }
    setSpeaking(false);
  }, []);

  const speak = useCallback(async (text, options = {}) => {
    if (!text) return;

    const {
      voice = 'en-US-Wavenet-D',
      rate = 0.9,
      pitch = 1.0,
      onEnd = null,
    } = options;

    stop();
    setSpeaking(true);

    const cacheKey = getCacheKey(text, voice, rate, pitch);

    try {
      let audioUrl;

      if (audioCache.has(cacheKey)) {
        audioUrl = audioCache.get(cacheKey);
      } else {
        const response = await fetch('/api/tts', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ text, voice, speakingRate: rate, pitch }),
        });

        if (!response.ok) throw new Error('TTS API failed');

        const blob = await response.blob();
        audioUrl = URL.createObjectURL(blob);

        if (audioCache.size >= MAX_CACHE) {
          const firstKey = audioCache.keys().next().value;
          URL.revokeObjectURL(audioCache.get(firstKey));
          audioCache.delete(firstKey);
        }
        audioCache.set(cacheKey, audioUrl);
      }

      const audio = new Audio(audioUrl);
      audioRef.current = audio;

      audio.onended = () => {
        setSpeaking(false);
        audioRef.current = null;
        if (onEnd) onEnd();
      };

      audio.onerror = () => {
        console.warn('[Finley TTS] Audio playback failed — falling back to browser speech.');
        setSpeaking(false);
        audioRef.current = null;
        fallbackSpeak(text, onEnd);
      };

      await audio.play();
    } catch (err) {
      console.warn('[Finley TTS] Google Cloud TTS failed — falling back to browser speech. Error:', err.message || err);
      setSpeaking(false);
      fallbackSpeak(text, onEnd);
    }
  }, [stop]);

  return { speak, stop, speaking };
}
